import React from "react";
import { UserCheck } from "lucide-react";
import type { IssuePerson } from "../../types/issue-triage";

interface OwnerPickerProps {
  /** The issue's current owner, if any. */
  owner?: IssuePerson;
  /** Assignable developers from useIssueOwners. */
  owners: IssuePerson[];
  onChange: (owner: IssuePerson | null) => void;
  disabled?: boolean;
  /** Small inline variant for Kanban cards. */
  compact?: boolean;
}

const UNOWNED = "";

/**
 * Owner select. Stops click/pointer propagation so choosing an owner on a
 * card does not also open the detail panel or start a drag.
 */
export function OwnerPicker({ owner, owners, onChange, disabled, compact }: OwnerPickerProps) {
  // Keep the current owner selectable even if they are no longer assignable
  // (deactivated), so the select never silently shows "Unowned" for them.
  const options =
    owner && !owners.some((o) => o.uid === owner.uid) ? [...owners, owner] : owners;

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <label
      className={`inline-flex items-center gap-1 ${compact ? "text-xs" : "text-sm"} text-slate-600`}
      onClick={stop}
      onPointerDown={stop}
      onKeyDown={stop}
    >
      <UserCheck className={compact ? "w-3 h-3" : "w-4 h-4"} aria-hidden="true" />
      <span className="sr-only">Owner</span>
      <select
        aria-label="Owner"
        value={owner?.uid ?? UNOWNED}
        disabled={disabled}
        onChange={(e) => {
          const next = options.find((o) => o.uid === e.target.value) ?? null;
          if ((next?.uid ?? UNOWNED) !== (owner?.uid ?? UNOWNED)) onChange(next);
        }}
        className={`rounded-md ring-1 ring-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#3FA5FF] disabled:opacity-60 ${
          compact ? "px-1 py-0.5 text-xs max-w-[110px]" : "px-2 py-1 text-sm"
        }`}
      >
        <option value={UNOWNED}>Unowned</option>
        {options.map((o) => (
          <option key={o.uid} value={o.uid}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
